<script lang="ts">
  import { page } from '$app/state';
  import Button from '$components/button/Button.svelte';
  import Page from '$components/page/Page.svelte';
  import { getCometh4337Wallet } from '$services/cometh/getComethWallet';
  import { getWalletOwners } from '$services/wallet/getWalletOwners';
  import { stateError } from '$states/error.svelte.js';
  import { type AppError, ErrorType } from '$types/error';
  import type { Signer } from '$types/owners';
  import { getMetriAddress } from '$utils/getMetriAddress';
  import type { Address, Hex } from 'viem';
  import Logo from '$lib/assets/metri-emblem-color.svg';
  import IconArrowRight from '$lib/icons/IconArrowRight.svelte';
  import IconGnosis from '$lib/icons/IconGnosis.svelte';
  import { NEW_DOMAIN } from '$lib/constants/domainMigration';

  let isLoading = $state(false);

  const params = $derived(page.url.searchParams);

  const signer: Signer = $derived({
    signerAddress: params.get('ad') as Address,
    deviceData: {
      browser: params.get('b') || '',
      os: params.get('os') || '',
      platform: params.get('p') || '',
    },
    publicKeyId: (params.get('id') as Hex) || undefined,
    publicKeyX: (params.get('x') as Hex) || undefined,
    publicKeyY: (params.get('y') as Hex) || undefined,
  });

  const handleAccept = async (): Promise<void> => {
    isLoading = true;

    try {
      if (signer.publicKeyId) {
        const { smartAccountClient } = await getCometh4337Wallet(getMetriAddress());
        await smartAccountClient.validateAddDevice({ signer });

        await getWalletOwners();

        isLoading = false;
        window.location.href = `https://${NEW_DOMAIN}/welcome?migrated=true`;
      } else {
        throw new Error('No public key id found');
      }
    } catch (error) {
      stateError.set({
        type: ErrorType.TOAST,
        rawError: error as AppError,
        message: 'Error validating migration passkey.',
      });
      isLoading = false;
    }
  };

  const deviceInfo = $derived(() => {
    const { browser, os } = signer.deviceData;
    if (os && browser) {
      return `${os}, ${browser}`;
    }
    if (browser) {
      return browser;
    }
    return 'Unknown device';
  });

  const handleHelp = (): void => {
    window.open('mailto:help@gnosis.io', '_blank');
  };
</script>

<Page>
  {#snippet middle()}
    <div class="p-gno-page pt-16">
      <div class="grid gap-4 text-center">
        <h1 class="typography-h2-bold">Confirm Passkey</h1>
        <span class="typography-body-l-medium text-foreground-muted">
          Confirm that the new app passkey<br />request is really you.
        </span>
      </div>
      <div
        class="bg-background-subtle rounded-2xl w-full mt-8 py-20 items-center justify-center gap-4 flex">
        <div class="size-18 rounded-full bg-background grid place-items-center shadow-lg">
          <img src={Logo} alt="Metri" class="size-10" />
        </div>
        <span><IconArrowRight /></span>
        <div class="size-18 rounded-full bg-blue-700 grid place-items-center shadow-lg">
          <IconGnosis class="text-foreground-alt-default size-10" />
        </div>
      </div>
      <div class="typography-body-l-medium grid items-center space-y-sm pb-sm w-full px-4 mt-6">
        <div class="flex items-center justify-between gap-2">
          <span class="text-foreground-subtle">Created on</span>
          <span>{deviceInfo()}</span>
        </div>
      </div>
    </div>
  {/snippet}
  {#snippet bottom()}
    <div class="p-gno-page grid gap-3">
      <p class="text-foreground-muted typography-body-m-medium my-3 text-center">
        This process can take up to a few minutes.
      </p>
      <Button ariaLabel="Migrate" variant="primary" pending={isLoading} onclick={handleAccept}>
        Accept
      </Button>
      <Button ariaLabel="Get Help" variant="plain" size="sm" onclick={handleHelp}>
        <span class="text-foreground-muted">Need help? Contact us at help@gnosis.io</span>
      </Button>
    </div>
  {/snippet}
</Page>
